import { useMemo, useState } from 'react';

import { Page, PageHeader } from '../components/layout/AppShell';
import { Card, CardHeader } from '../components/ui/Card';
import { SegmentedControl } from '../components/ui/Button';
import { Select } from '../components/ui/Field';
import { AsyncBoundary, EmptyState, Skeleton } from '../components/ui/AsyncBoundary';
import { StatTile } from '../components/charts/StatTile';
import { ChartCard, rangeToWindow } from '../components/charts/ChartCard';
import { ColumnChart } from '../components/charts/ColumnChart';
import { StackedBar, BarList } from '../components/charts/StackedBar';
import { useResource } from '../hooks/useResource';
import { HEALTH_BANDS } from '../lib/domain';
import * as fmt from '../lib/format';

/**
 * Fleet analytics.
 *
 * Everything here reads device_state, the trips table or the pre-aggregated
 * rollups — never raw telemetry. That is the difference between a page that
 * loads in a moment and one that times out once the fleet is real.
 */
export function Analytics() {
  const [range, setRange] = useState('30d');
  const [vehicleId, setVehicleId] = useState('');

  const window = useMemo(() => rangeToWindow(range), [range]);

  const fleet = useResource('/api/analytics/fleet', null, { pollMs: 60_000 });
  const health = useResource('/api/analytics/health');
  const vehicles = useResource('/api/vehicles', { limit: 500 });
  const perVehicle = useResource(
    vehicleId ? `/api/analytics/vehicles/${vehicleId}` : null,
    { from: window.from, to: window.to }
  );

  return (
    <Page className="space-y-4">
      <PageHeader
        title="Analytics"
        subtitle="Utilisation and device health across your scope"
        actions={
          <SegmentedControl
            value={range}
            onChange={setRange}
            options={[
              { value: '7d', label: '7D' },
              { value: '30d', label: '30D' },
            ]}
          />
        }
      />

      <AsyncBoundary state={fleet} skeleton={<Skeleton className="h-24 w-full" />}>
        {(data) => (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <StatTile label="Vehicles" value={data.vehicles.vehicles} icon="truck" tone="brand" compact />
            <StatTile label="Reporting" value={data.vehicles.online} icon="signal" tone="good" compact />
            <StatTile
              label="Average health"
              value={data.vehicles.avg_health ? Math.round(data.vehicles.avg_health) : '—'}
              icon="shield"
              tone="brand"
              compact
            />
            <StatTile
              label="Distance today"
              value={Number(data.today.distance_km)}
              unit="km"
              icon="route"
              tone="accent"
              compact
            />
            <StatTile
              label="Alerts (24h)"
              value={data.alerts.last_24h}
              icon="bell"
              tone={data.alerts.critical > 0 ? 'critical' : 'warning'}
              compact
            />
          </div>
        )}
      </AsyncBoundary>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader title="Fleet health" subtitle="Devices by band" icon="shield" />
          <div className="p-4">
            <AsyncBoundary state={health} skeleton={<Skeleton className="h-32" />}>
              {(data) => (
                <div className="space-y-5">
                  <StackedBar
                    height={12}
                    segments={(data.bands || []).map((row) => ({
                      key: row.band,
                      label: HEALTH_BANDS[row.band]?.label || row.band,
                      tone: HEALTH_BANDS[row.band]?.tone || 'neutral',
                      value: Number(row.devices),
                    }))}
                  />
                  {(data.flags || []).length > 0 && (
                    <div>
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-3">
                        Failing signals
                      </p>
                      <BarList
                        items={(data.flags || []).map((row) => ({
                          key: row.flag,
                          label: row.flag.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
                          value: Number(row.devices),
                          tone: 'warning',
                        }))}
                      />
                    </div>
                  )}
                </div>
              )}
            </AsyncBoundary>
          </div>
        </Card>

        <Card>
          <CardHeader title="Firmware in the field" subtitle="Rollout coverage" icon="chip" />
          <div className="p-4">
            <AsyncBoundary state={health} skeleton={<Skeleton className="h-32" />}>
              {(data) => {
                const versions = data.firmware || [];
                if (!versions.length) {
                  return <EmptyState icon="chip" title="No devices reporting a version yet" />;
                }

                // A single-bar bar chart is not a chart. When the whole fleet
                // is on one version, the interesting fact is that sentence.
                if (versions.length === 1) {
                  return (
                    <div className="flex items-baseline gap-2">
                      <span className="text-[28px] font-semibold tracking-tight text-ink">
                        {versions[0].devices}
                      </span>
                      <p className="text-[13.5px] text-ink-2">
                        device{Number(versions[0].devices) === 1 ? '' : 's'}, all on{' '}
                        <span className="font-mono text-ink">
                          {versions[0].firmware_version || 'an unknown version'}
                        </span>
                        . No rollout in progress.
                      </p>
                    </div>
                  );
                }

                return (
                  <BarList
                    items={versions.map((row) => ({
                      key: row.firmware_version || 'unknown',
                      label: row.firmware_version || 'Unknown',
                      value: Number(row.devices),
                    }))}
                  />
                );
              }}
            </AsyncBoundary>
          </div>
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Per-vehicle utilisation"
          subtitle={`${window.label} · choose a vehicle`}
          icon="chart"
          actions={
            <Select
              size="sm"
              value={vehicleId}
              onChange={(event) => setVehicleId(event.target.value)}
              placeholder="Choose a vehicle"
              className="w-56"
              options={(vehicles.data?.vehicles || []).map((v) => ({
                value: v.id,
                label: v.reg_number,
              }))}
            />
          }
        />
        <div className="p-4">
          {!vehicleId ? (
            <EmptyState
              className="py-8"
              icon="truck"
              title="Choose a vehicle"
              description="Pick a registration above to see its distance, driving time and daily pattern."
            />
          ) : (
            <AsyncBoundary state={perVehicle} skeleton={<Skeleton className="h-56" />}>
              {(data) => {
                const daily = (data.daily || []).map((row) => ({
                  key: row.day,
                  label: new Date(row.day).toLocaleDateString(undefined, {
                    day: '2-digit',
                    month: 'short',
                  }),
                  value: Number(row.distance_km),
                  tooltipLabel: 'Distance (km)',
                }));

                return (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                      <StatTile label="Trips" value={data.totals.trips} icon="route" tone="brand" compact />
                      <StatTile
                        label="Distance"
                        value={Number(data.totals.distance_km)}
                        unit="km"
                        icon="route"
                        tone="accent"
                        compact
                      />
                      <StatTile
                        label="Driving"
                        value={fmt.duration(data.totals.driving_s)}
                        icon="clock"
                        tone="brand"
                        compact
                      />
                      <StatTile
                        label="Idle"
                        value={fmt.duration(data.totals.idle_s)}
                        icon="pause"
                        tone="warning"
                        compact
                      />
                      <StatTile
                        label="Top speed"
                        value={Math.round(data.totals.max_speed || 0)}
                        unit="km/h"
                        icon="arrow"
                        tone="accent"
                        compact
                      />
                    </div>

                    <ChartCard
                      title={`Distance per day — ${data.regNumber}`}
                      subtitle={`${window.label} · kilometres`}
                      icon="chart"
                      table={{
                        columns: [
                          { key: 'label', header: 'Day' },
                          { key: 'value', header: 'km', align: 'right' },
                        ],
                        rows: daily,
                      }}
                    >
                      <ColumnChart
                        data={daily}
                        height={220}
                        highlightLast
                        ariaLabel={`Distance per day for ${data.regNumber}`}
                        formatValue={(v) => String(Math.round(v))}
                        emptyMessage="No completed trips in this range"
                      />
                    </ChartCard>
                  </div>
                );
              }}
            </AsyncBoundary>
          )}
        </div>
      </Card>
    </Page>
  );
}
